import cn from 'classnames'
import {FC, useRef, useState} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {ChevronIcon} from '@/components/common/icons/ChevronIcon'
import {SearchIcon} from '@/components/common/icons/SearchIcon'
import {getTmAcademyContent} from '@/content/tm-academy/tmAcademy'
import {Link, useTranslation} from '@/i18n'
import {HOVER_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {useNavigation} from '@/utils/hooks/useNavigation'
import {useLanguage} from '@/utils/i18n/language'

type KnowledgeBaseHeaderSearchProps = {
  data: {
    title: string
    linkHref: string
  }[]
  query: string
  setQuery: (value: string) => void
}

export const KnowledgeBaseHeaderSearch: FC<KnowledgeBaseHeaderSearchProps> = ({
  data,
  query,
  setQuery,
}) => {
  const {t} = useTranslation()

  const {push} = useNavigation()
  const language = useLanguage()
  const {trySearchingForTitle, noResultsTitle} = getTmAcademyContent(language).header
  const inputRef = useRef<HTMLInputElement>(null)
  const [isSearchActive, setSearchActive] = useState(false)

  const handleFocus = () => {
    if (isSearchActive) {
      setSearchActive(false)
    } else {
      setQuery('')
      setSearchActive(true)
    }
  }

  const handleClickOnLink = () => {
    setSearchActive(false)
  }

  return (
    <>
      <div className="wrapper">
        <div className="search-icon">
          <SearchIcon color={theme.palette.text.white} />
        </div>
        <div className="search">
          <input
            id="search-input"
            type="text"
            value={query}
            onFocus={() => handleFocus()}
            onChange={(e) => setQuery(e.target.value)}
            className="input"
            ref={inputRef}
            autoComplete="off"
            onBlur={() => {
              setSearchActive(false)
            }}
            onKeyPress={(e) => e.key === 'Enter' && e.preventDefault()}
          />
          <div className={cn('results', isSearchActive && 'visible')}>
            {!query.trim() && (
              <div className="title">
                <Text variant={TextVariant.subtitle11} color={theme.palette.gray[300]}>
                  {t(trySearchingForTitle)}
                </Text>
              </div>
            )}
            <ul className="list">
              {data.length > 0 ? (
                data.map(
                  ({title, linkHref}, i) =>
                    i < 4 && (
                      <li
                        key={title}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          push(linkHref)
                          setTimeout(() => handleClickOnLink(), HOVER_TRANSITION)
                        }}
                        role="presentation"
                      >
                        {/* The dropdown is rendered while closed, so these would prefetch unseen. */}
                        <Link href={linkHref} prefetch={false} legacyBehavior>
                          <a className="link">
                            <Text
                              variant={TextVariant.text14}
                              fontWeight={TextFontWeight.semibold}
                              color={theme.palette.text.white}
                            >
                              {title}
                            </Text>
                            <div className="chevron">
                              <ChevronIcon variant="right" color={theme.palette.text.white} />
                            </div>
                          </a>
                        </Link>
                      </li>
                    ),
                )
              ) : (
                <div className="no-results">
                  <Text variant={TextVariant.text14} color={theme.palette.text.white}>
                    {t(noResultsTitle)}
                  </Text>
                </div>
              )}
            </ul>
          </div>
        </div>
      </div>

      <style jsx>{`
        .wrapper {
          display: flex;
          align-items: center;
          margin-right: 10px;
        }
        .search {
          position: relative;
          margin-left: ${theme.spacing[1.5]};
        }
        .search-icon {
          margin-top: ${theme.spacing[1]};
        }
        .input {
          ${theme.typography.text14.desktop};
          width: 240px;
          padding: 0;
          color: ${theme.palette.text.white};
          background-color: transparent;
          border-bottom: 1px solid ${theme.palette.gray[600]};
          transition: width ${HOVER_TRANSITION}ms ease-in-out;
        }
        .results {
          visibility: hidden;
          width: 240px;
          right: 0;
          opacity: 0;
          position: absolute;
          background-color: ${theme.palette.tertiary.main};
          border: 1px solid ${theme.palette.gray[600]};
          margin-top: ${theme.spacing[2]};
          border-radius: 8px;
          box-shadow: 0px 12px 48px 16px rgba(0, 0, 0, 0.03), 0px 9px 28px rgba(0, 0, 0, 0.05),
            0px 6px 16px -8px rgba(0, 0, 0, 0.08);
          transition: visibility ${HOVER_TRANSITION}ms ease-in-out,
            opacity ${HOVER_TRANSITION}ms ease-in-out;
        }
        .title {
          padding: ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[1]};
        }
        .list {
          margin: ${theme.spacing[2]} ${theme.spacing[3]};
        }
        .no-results {
          padding: ${theme.spacing[2]} 0;
        }
        .link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: ${theme.spacing[2]} ${theme.spacing[3]};
          margin: 0 -${theme.spacing[3]};
          transition: background-color ${HOVER_TRANSITION}ms ease-in-out;
        }
        .link:hover,
        .link:focus {
          background-color: ${theme.palette.gray[800]};
        }
        .chevron {
          margin-left: ${theme.spacing[2]};
          margin-right: -${theme.spacing[2]};
        }
        .visible {
          visibility: visible;
          opacity: 1;
        }
      `}</style>
    </>
  )
}
